import { useEffect, useRef } from 'react';
import type { Document } from '@/api/chat/getChatDocuments';
import { restoreSessionDocuments, useAppDispatch } from '@/store';
import type { ChatMessage } from '@/types/chat';
import { FileStatus } from '@/types/file';

/**
 * Chat documents not attached to any message (never sent, or the send failed). `messages` covers a
 * BE race where just-sent attachments briefly have no `messageId`.
 */
export function resolveUnsentChatDocuments(documents: Document[], messages: ChatMessage[]): Document[] {
  const messageAttachedIds = new Set(
    messages.flatMap((message) => (message.attachedDocuments ?? []).map((doc) => doc.documentId)),
  );

  return documents.filter((doc) => !doc.messageId && !messageAttachedIds.has(doc.documentId));
}

/** Puts unsent chat documents back into the input box, e.g. after a refresh. */
export function useRestoreUnsentChatDocuments({
  chatId,
  documents,
  messages,
  canEditChat,
}: {
  chatId: string | undefined;
  documents: Document[] | undefined;
  messages: ChatMessage[];
  canEditChat: boolean | undefined;
}) {
  const dispatch = useAppDispatch();
  // Restore each document once per chat — the cached list isn't refetched on delete, so a removed
  // document would come back.
  const restoredIdsRef = useRef<Set<string>>(new Set());
  const chatIdRef = useRef(chatId);

  useEffect(() => {
    if (chatIdRef.current !== chatId) {
      chatIdRef.current = chatId;
      restoredIdsRef.current = new Set();
    }

    if (!chatId || canEditChat === false || !documents?.length) return;

    const toRestore = resolveUnsentChatDocuments(documents, messages).filter(
      (doc) => !restoredIdsRef.current.has(doc.documentId),
    );

    if (toRestore.length === 0) return;

    for (const doc of toRestore) {
      restoredIdsRef.current.add(doc.documentId);
    }

    dispatch(
      restoreSessionDocuments(
        toRestore.map((doc) => ({
          documentId: doc.documentId,
          documentName: doc.documentName,
          uploadedAt: doc.uploadedAt,
          sizeMb: doc.sizeMb,
          // Processing state is unknown after a reload; the file status poller
          // resolves this to READY_FOR_CHAT (or an error status) on its first tick.
          status: FileStatus.UPLOADED,
        })),
      ),
    );
  }, [canEditChat, chatId, dispatch, documents, messages]);
}
